import { TreeNode } from 'primeng/api';
import { Table } from 'primeng/table';
import { ModuleProductPricingConfigRoundingRule, TreeFindResult, TreeLike } from '../interfaces/interfaces';

export function generateDeleteText(): string {
    return 'TÖRLÉS' + (Math.round(Math.random() * 8999) + 1000);
}

export function refreshTable(table: Table): void {
    table.onLazyLoad.next(table.createLazyLoadMetadata());
}

export function getTreeNodeParentNodeIdAndBeforeNodeIdByNodeId<T extends TreeLike>(tree: TreeNode<T>[], idToFind: number, parentNode: TreeNode<T> = null): TreeFindResult {
    for (let treeNode of tree) {
        if (treeNode.data.id === idToFind) {
            const index: number = tree.indexOf(treeNode);
            return {
                beforeNodeId: index === 0 ? null : tree[index - 1].data.id,
                parentNodeId: parentNode === null ? null : parentNode.data.id
            }
        }
        if (treeNode.children.length > 0) {
            const treeFindResult: TreeFindResult = getTreeNodeParentNodeIdAndBeforeNodeIdByNodeId(treeNode.children, idToFind, treeNode);
            if (treeFindResult !== null) {
                return treeFindResult;
            }
        }
    }
    return null;
}

export function convertToTreeNodes<T extends TreeLike>(data: T[]): TreeNode<T>[] {
    return data.map((d) => ({
        children: d.children.length > 0 ? convertToTreeNodes(d.children as T[]) : [],
        label: d.name,
        data: d
    }));
}

export function convertToTreeNodesDepth<T extends TreeLike>(data: T[], depth: number = 0): TreeNode<T>[] {
    return data.map((d) => ({
        children: d.children.length > 0 ? convertToTreeNodesDepth(d.children as T[], depth + 1) : [],
        label: d.name,
        data: {
            ...d,
            depth
        }
    }));
}

export function getSelectedTreeNodes<T extends TreeLike>(treeNodes: TreeNode<T>[], selectedIds: (number | string)[] = []): TreeNode<T>[] {
    if (treeNodes.length === 0) {
        return [];
    }
    const selectedTreeNodes: TreeNode<T>[] = [];
    treeNodes.forEach((treeNode: TreeNode<T>) => {
        if (treeNode.data !== undefined && selectedIds.indexOf(treeNode.data.id) !== -1) {
            selectedTreeNodes.push(treeNode);
        }
        selectedTreeNodes.push(...getSelectedTreeNodes(treeNode.children || [], selectedIds));
    });
    return selectedTreeNodes;
}

export function getOpenedTreeNodeIds<T extends TreeLike>(treeNodes: TreeNode<T>[]): (number|string)[] {
    const ids: (number|string)[] = [];
    treeNodes.forEach((treeNode: TreeNode<T>) => {
        if (treeNode.expanded && treeNode.data !== undefined) {
            ids.push(treeNode.data.id);
        }
        ids.push(...getOpenedTreeNodeIds(treeNode.children || []));
    });
    return ids;
}

export function expandTreeNodesByIds<T extends TreeLike>(treeNodes: TreeNode<T>[], ids: (number|string)[]): void {
    treeNodes.forEach((treeNode: TreeNode<T>) => {
        if (treeNode.data !== undefined && ids.indexOf(treeNode.data.id) !== -1) {
            treeNode.expanded = true;
        }
        expandTreeNodesByIds(treeNode.children || [], ids);
    });
}

export function treePartialNodes<T extends TreeLike>(treeNodes: TreeNode<T>[], selectedTreeNodes: TreeNode[], expand: boolean = false): void {
    treeNodes.forEach((treeNode: TreeNode<T>) => {
        treeNode.partialSelected = hasSelectedChildren(treeNode, selectedTreeNodes);
        treeNode.expanded = treeNode.partialSelected ? expand : false;
        treePartialNodes(treeNode.children || [], selectedTreeNodes, expand);
    });
}

export function treeSelectOnlyLeafs<T extends TreeLike>(treeNodes: TreeNode<T>[]): void {
    treeNodes.forEach((treeNode: TreeNode<T>) => {
        if (treeNode.children !== undefined && treeNode.children.length !== 0) {
            treeNode.selectable = false;
        }
        treeSelectOnlyLeafs(treeNode.children || []);
    });
}

export function hasSelectedChildren(treeNode: TreeNode, selectedTreeNodes: TreeNode[]): boolean {
    return selectedTreeNodes.indexOf(treeNode) !== -1 || (treeNode.children || []).some(node => hasSelectedChildren(node, selectedTreeNodes));
}

export function formatDate(date: Date|null = null): string|null {
    if (date instanceof Date) {
        return date.getFullYear() +
            '-' + (date.getMonth() + 1).toString().padStart(2, '0') +
            '-' + date.getDate().toString().padStart(2, '0') +
            'T' + date.getHours().toString().padStart(2, '0') +
            ':' + date.getMinutes().toString().padStart(2, '0') +
            ':' + date.getSeconds().toString().padStart(2, '0') +
            '.' + (date.getMilliseconds() / 1000).toFixed(3).slice(2, 5) +
            'Z';
    }
    return null;
}

export function formatDateS(date: string | Date): Date|null {
    if (typeof date === 'string') {
        date = date.replace('Z', '');
        date = date.replace('T', ' ');
        return new Date(date);
    }
    if (date instanceof Date) {
        return date;
    }
    return null;
}

export function formatDateR(date: string): string {
    return date.replace('T', ' ').replace('Z', '');
}

export function onlyUniqueString(value: string, index: number, self: string[]): boolean {
    return self.indexOf(value) === index;
}

export function replaceAll(input: string, find: string, replace: string): string {
    return input.replace(new RegExp(find, 'g'), replace);
}

export function parseNumber(input: string | number | null, defaultValue: number = 0): number {
    if (typeof input === 'number') {
        if (input === Infinity || isNaN(input)) {
            return defaultValue;
        }
        return input;
    }
    if (typeof input === 'string') {
        const regex: RegExp = /^-?[\d, .]+$/g;
        if (!input.match(regex) === null) {
            return defaultValue;
        }
        input = input.replace(',', '.');
        input = replaceAll(input, ' ', '');
        input = Number(input);
        if (input === Infinity || isNaN(input)) {
            return defaultValue;
        }
        return input;
    }
    return defaultValue;
}

export function randomIntFromInterval(min: number, max: number | null = null): number {
    const minNumber: number = parseNumber(min, 0);
    let maxNumber: number = 0;
    if (max === null) {
        maxNumber = min * 2;
    } else {
        maxNumber = parseNumber(max, 0);
    }
    const randomInt: number = Math.floor((Math.random() * ((maxNumber - minNumber) + 1)) + minNumber);
    return randomInt;
}

export function validIp(ipaddress: string): boolean {
    return /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(ipaddress);
}